import Head from 'next/head';
import { useRouter } from 'next/router';
import ErrorPage from 'next/error';
import { gql } from '@apollo/client';
import { useQuery } from '@helpers/async';
import {
  Container,
  Box,
  Text,
  Flex,
  Grid,
  GridItem,
  Heading,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react';
import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
} from 'react-share';
import NextNprogress from 'nextjs-progressbar';

import Layout from '@components/resources/Layout';
import RelatedPosts from '@components/resources/RelatedPosts';
import Blockquote from '@components/resources/Blockquote';
import Callout from '@components/resources/Callout';
import BlueCTA from '@components/resources/BlueCTA';
import Companies from '@components/resources/Companies';
import ImageShimmer from '@components/ImageShimmer/ImageShimmer';
import BuildReportContactHandler from '@components/Forms/BuildReportContactHandler';
import GuidesEngineersForm from '@components/resources/Forms/GuidesEngineersForm';
import GutenbergStylesheet from '../../styles/gutenberg';
import { useGetUtmCookies } from '../../hooks/utmCookies';

import sharecardImage from '@public/images/resources/sharecard.jpg';
import favicon from '@public/favicon.ico';
import sitePath from '@helpers/sitePath';

import {
  primaryCategory,
  filterPostsByCategory,
  filterSinglePostByCategory,
} from '@helpers/categoryFilters';
import { addParamsToRelatedPost } from '@helpers/addParamsToRelatedPost';
import { determineMetaRobots } from '@helpers/metaRobots';
import { ArticleJsonLd, NextSeo } from 'next-seo';
import { ArrowLeftIcon, Button, Link } from '@terminal/design-system';

export const GET_GUIDE = gql`
  query GetGuide($slug: ID!) {
    guide(id: $slug, idType: SLUG) {
      id
      date
      title
      seo {
        title
        metaDesc
        metaRobotsNofollow
        metaRobotsNoindex
      }
      guidePostType {
        guideType
        conversion
        headline {
          text
        }
        hero {
          text
        }
        content {
          icon {
            altText
            sourceUrl
          }
          title
          text
          list {
            title
            items {
              item
            }
          }
        }
        download {
          title
          thumbnail {
            altText
            sourceUrl
          }
          pardotFormUrl
        }
        quoteCallout {
          type
          callout
          quote {
            author
            text
          }
        }
      }
      categories {
        edges {
          isPrimary
          node {
            name
          }
        }
      }
      featuredImage {
        node {
          altText
          sourceUrl
        }
      }
      relatedPosts {
        posts {
          ... on Post {
            id
            slug
            title
            categories {
              edges {
                isPrimary
                node {
                  name
                }
              }
            }
            featuredImage {
              node {
                altText
                sourceUrl
              }
            }
          }
          ... on Guide {
            id
            slug
            title
            categories {
              edges {
                isPrimary
                node {
                  name
                }
              }
            }
            featuredImage {
              node {
                altText
                sourceUrl
              }
            }
            guidePostType {
              guideType
              externalLink {
                url
              }
            }
          }
        }
      }
    }
    posts(first: 100) {
      nodes {
        id
        date
        slug
        title
        categories {
          edges {
            isPrimary
            node {
              name
            }
          }
        }
        featuredImage {
          node {
            altText
            sourceUrl
          }
        }
      }
    }
    guides(first: 100) {
      nodes {
        id
        date
        slug
        title
        categories {
          edges {
            isPrimary
            node {
              name
            }
          }
        }
        featuredImage {
          node {
            altText
            sourceUrl
          }
        }
        guidePostType {
          guideType
          externalLink {
            url
          }
        }
      }
    }
  }
`;

export default function Blog() {
  const router = useRouter();

  const utmParams = useGetUtmCookies();

  const { slug } = router.query;

  // Client side Apollo
  const { loading, error, data } = useQuery(GET_GUIDE, {
    variables: {
      slug,
    },
  });

  // CSR loading
  if (loading)
    return (
      <NextNprogress
        color="#2D445D"
        startPosition={0.3}
        stopDelayMs={200}
        height={3}
        showOnShallow={true}
      />
    );
  // Apollo error
  if (error) return <ErrorPage statusCode="500" />;

  const post = data.guide;
  const relatedPosts = data.guide ? data.guide.relatedPosts.posts : null;

  // get all post types
  const allResources = [].concat(data.posts.nodes, data.guides.nodes);

  // sort by date
  const allSortedResources = allResources.sort((a, b) => (a.date < b.date ? 1 : -1));

  const latestPosts = filterPostsByCategory(allSortedResources, primaryCategory(post)).slice(0, 4);

  // add path for post type
  let relatedPostsWithParams = null;

  if (relatedPosts) {
    relatedPostsWithParams = addParamsToRelatedPost(relatedPosts);
  } else {
    relatedPostsWithParams = addParamsToRelatedPost(latestPosts);
  }

  const postImage = post.featuredImage ? post.featuredImage.node.sourceUrl : sharecardImage.src;

  const type = filterSinglePostByCategory(post, 'Supply') ? 'engineers' : 'businesses';

  const conversion = post.guidePostType.conversion || '';
  const headingTitle = post.guidePostType.headline.text
    ? post.guidePostType.headline.text
    : post.title;

  return (
    <>
      <NextSeo
        title={post.seo.title}
        description={post.seo.metaDesc}
        canonical={sitePath(router.asPath)}
        {...determineMetaRobots({ translated: false, locale: router.locale, post })}
        openGraph={{
          type: 'article',
          siteName: 'Terminal.io',
          url: sitePath(router.asPath),
          article: {
            publishedTime: post.date,
            section: primaryCategory(post),
            tags: post.categories.edges.map((category) => category.node.name),
            authors: [post?.author?.node?.name],
          },
          images: [
            {
              url: postImage,
              alt: postImage.altText || post.seo.title,
            },
          ],
        }}
      />
      <ArticleJsonLd
        type="Article"
        useAppDir={false}
        url={sitePath(router.asPath)}
        title={post.seo.title}
        images={[postImage]}
        datePublished={post.date}
        authorName={[
          {
            name: post?.author?.node?.name,
          },
        ]}
        publisherName="terminal.io"
        publisherLogo={favicon.src}
        description={post.seo.metaDesc}
        isAccessibleForFree={true}
      />
      <GutenbergStylesheet />
      <Layout type={type}>
        <Container fontFamily="body">
          <Box py={3}>
            <Button
              as={Link}
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeftIcon />}
              href={
                filterSinglePostByCategory(post, 'Demand')
                  ? '/resources'
                  : '/engineers/the-remote-engineer'
              }
            >
              Go back to{' '}
              {filterSinglePostByCategory(post, 'Demand') ? 'Resources' : 'The Remote Engineer'}
            </Button>
          </Box>
          <Grid
            templateColumns={{ base: '1fr', lg: '26.875rem 1fr' }}
            gap={{ base: '3rem', lg: '6.5625rem' }}
            mb={{ base: '2.8rem', md: '3.5rem' }}
          >
            <GridItem>
              <Text
                variant="hint"
                color="text.secondary"
                textTransform="uppercase"
                letterSpacing="0.0625rem"
              >
                {primaryCategory(post)} | Guide
              </Text>
              <Heading
                as="h1"
                fontSize={{
                  base: '1.8rem',
                  md: '2.25rem',
                }}
                fontWeight="600"
                lineHeight={{
                  base: '2rem',
                  md: '2.5rem',
                }}
                mt="1rem"
                mb={{ base: '1.2rem', md: '1.5rem' }}
                color="#2D445D"
              >
                {headingTitle}
              </Heading>
              {post.guidePostType.hero.text && (
                <Text
                  mb={{ base: '2rem', md: '2.5rem' }}
                  fontSize="0.9375rem"
                  lineHeight="1.5rem"
                  dangerouslySetInnerHTML={{
                    __html: post.guidePostType.hero.text,
                  }}
                />
              )}

              <Box
                w="100%"
                height={{ base: '0.0625rem' }}
                m={{ base: '1rem 0' }}
                bgColor="#D8D8D8"
              />
              <Flex
                fontFamily="var(--chakra-fonts-body)"
                fontWeight="600"
                fontSize="1rem"
                lineHeight="1.25rem"
                mt={{ base: '0.5rem' }}
                flexWrap="wrap"
              >
                <Box
                  fontFamily="body"
                  fontSize="0.6875rem"
                  lineHeight="1.25rem"
                  fontWeight="600"
                  color="#909090"
                  textTransform="uppercase"
                  letterSpacing="0.0625rem"
                  mr={{ base: '1rem', md: '2rem' }}
                >
                  Share
                </Box>
                <Box mr={{ base: '0.75rem', sm: '1rem', md: '2rem' }}>
                  <FacebookShareButton url={sitePath(router.asPath)} quote={post.title}>
                    Facebook
                  </FacebookShareButton>
                </Box>
                <Box mr={{ base: '0.75rem', sm: '1rem', md: '2rem' }}>
                  <TwitterShareButton url={sitePath(router.asPath)} title={post.title}>
                    Twitter
                  </TwitterShareButton>
                </Box>
                <Box mr={{ base: '0.75rem', sm: '1rem', md: '2rem' }}>
                  <LinkedinShareButton url={sitePath(router.asPath)}>LinkedIn</LinkedinShareButton>
                </Box>
                <Box mr={{ base: '0.75rem', sm: '1rem', md: '2rem' }}>
                  <EmailShareButton
                    url={sitePath(router.asPath)}
                    subject={post.title}
                    body={post.title}
                    openShareDialogOnClick={true}
                    onClick={(e) => e.preventDefault()}
                  >
                    Email
                  </EmailShareButton>
                </Box>
              </Flex>
              <Box
                w="100%"
                height={{ base: '0.0625rem' }}
                m={{ base: '1rem 0' }}
                bgColor="#D8D8D8"
              />

              <Flex mt={{ base: '2.6rem', md: '3.25rem' }} mb={{ base: '1.2rem', md: '1.5rem' }}>
                {post.guidePostType.content.icon && (
                  <Box
                    width={{ base: '2.8rem', md: '3.5rem' }}
                    height={{ base: '2.55rem', md: '3.1875rem' }}
                    mr={{ base: '1.3rem', md: '1.625rem' }}
                    flexShrink="0"
                    position="relative"
                  >
                    <ImageShimmer
                      src={post.guidePostType.content.icon.sourceUrl}
                      alt={post.guidePostType.content.icon.altText}
                      style={{ objectFit: 'cover' }}
                      fill
                    />
                  </Box>
                )}
                {post.guidePostType.content.title && (
                  <Heading
                    as="h2"
                    fontSize={{ base: '1.4rem', md: '1.75rem' }}
                    lineHeight={{ base: '1.5rem', md: '1.875rem' }}
                    color="#2D445D"
                  >
                    {post.guidePostType.content.title}
                  </Heading>
                )}
              </Flex>
              {post.guidePostType.content.text && (
                <Text
                  mb={{ base: '2.4rem', md: '3rem' }}
                  fontSize="0.9375rem"
                  lineHeight="1.5rem"
                  dangerouslySetInnerHTML={{
                    __html: post.guidePostType.content.text,
                  }}
                />
              )}
              {post.guidePostType.content.list.title && (
                <Box
                  mb="1rem"
                  color="#2D445D"
                  fontWeight="700"
                  textTransform="uppercase"
                  fontSize="1rem"
                  lineHeight="1.125rem"
                >
                  {post.guidePostType.content.list.title}
                </Box>
              )}
              {post.guidePostType.content.list.items && (
                <UnorderedList>
                  {post.guidePostType.content.list.items.map((item) => (
                    <ListItem
                      key={item.item}
                      mb="0.25rem"
                      fontSize="0.9375rem"
                      lineHeight="1.5rem"
                      dangerouslySetInnerHTML={{
                        __html: item.item,
                      }}
                    />
                  ))}
                </UnorderedList>
              )}
            </GridItem>
            <GridItem>
              <Box
                bgColor="#F6F9FA"
                padding={{ base: '2rem 3rem', md: '2.5rem 5rem' }}
                maxW="35rem"
              >
                {post.guidePostType.download.title && (
                  <Box
                    fontWeight="700"
                    fontSize="1.25rem"
                    lineHeight="1.625rem"
                    color="#2D445D"
                    mb="2rem"
                    dangerouslySetInnerHTML={{
                      __html: post.guidePostType.download.title,
                    }}
                  />
                )}
                {post.guidePostType.download.thumbnail && (
                  <Box
                    w="14rem"
                    h={{
                      base: '10.8125rem',
                    }}
                    ml="auto"
                    mr="auto"
                    mb={{ base: '1.6rem', lg: '2rem' }}
                    position="relative"
                    boxShadow="0.3125rem 0.3125rem 0.75rem rgba(0, 0, 0, 0.16)"
                  >
                    <ImageShimmer
                      src={post.guidePostType.download.thumbnail.sourceUrl}
                      alt={post.guidePostType.download.thumbnail.altText}
                      style={{ objectFit: 'cover' }}
                      fill
                    />
                  </Box>
                )}
                {filterSinglePostByCategory(post, 'Supply') ? (
                  <GuidesEngineersForm conversion={conversion} />
                ) : (
                  post.guidePostType.download.pardotFormUrl && (
                    <Box p={{ base: '4', sm: '4' }}>
                      <BuildReportContactHandler
                        buttonTitle="GET GUIDE"
                        thankYouUrl={`/guides/thank-you?slug=${slug}`}
                        formType={slug}
                        theme={{ green200: '#87b75c' }}
                        completed={false}
                        showLabels={true}
                      />
                    </Box>
                  )
                )}
              </Box>
            </GridItem>
          </Grid>

          {!post.guidePostType.quoteCallout.type && post.guidePostType.quoteCallout.quote.text && (
            <Blockquote
              text={post.guidePostType.quoteCallout.quote.text}
              author={post.guidePostType.quoteCallout.quote.author}
            />
          )}

          {post.guidePostType.quoteCallout.type && post.guidePostType.quoteCallout.callout && (
            <Callout text={post.guidePostType.quoteCallout.callout} />
          )}
        </Container>

        <RelatedPosts
          title="Recommended reading"
          filteredOutId={post.id}
          posts={relatedPostsWithParams}
        />

        <BlueCTA type="engineers" />
        {!filterSinglePostByCategory(post, 'Supply') && <Companies />}
      </Layout>
    </>
  );
}
